import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

export type StaffRole = "server" | "driver";

export interface StaffRow {
  id: string;
  first_name: string;
  last_name: string | null;
  phone: string | null;
  email: string | null;
  roles: StaffRole[];
  notes: string | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface StaffInput {
  first_name: string;
  last_name?: string | null;
  phone?: string | null;
  email?: string | null;
  roles?: StaffRole[];
  notes?: string | null;
  is_active?: boolean;
}

/** Full name for display: "Cristina P" with null-safe joining. */
export function staffFullName(s: Pick<StaffRow, "first_name" | "last_name">): string {
  return [s.first_name, s.last_name].filter(Boolean).join(" ").trim();
}

export function useStaff(options?: { activeOnly?: boolean; role?: StaffRole; enabled?: boolean }) {
  return useQuery({
    queryKey: ["staff", options?.activeOnly ?? false, options?.role ?? null],
    queryFn: async () => {
      let q = supabase.from("staff").select("*").order("first_name", { ascending: true });
      if (options?.activeOnly) q = q.eq("is_active", true);
      if (options?.role) q = q.contains("roles", [options.role]);
      const { data, error } = await q;
      if (error) throw error;
      return (data ?? []) as StaffRow[];
    },
    enabled: options?.enabled ?? true,
  });
}

export function useCreateStaff() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: StaffInput) => {
      const { data, error } = await supabase
        .from("staff")
        .insert({
          first_name: input.first_name.trim(),
          last_name: input.last_name?.trim() || null,
          phone: input.phone?.trim() || null,
          email: input.email?.trim() || null,
          roles: input.roles ?? [],
          notes: input.notes?.trim() || null,
          is_active: input.is_active ?? true,
        })
        .select()
        .single();
      if (error) throw error;
      return data as StaffRow;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["staff"] });
      toast.success("Staff member added");
    },
    onError: (e: Error) => toast.error(e.message),
  });
}

export function useUpdateStaff() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...patch }: { id: string } & Partial<StaffInput>) => {
      const { error } = await supabase
        .from("staff")
        .update({
          ...(patch.first_name !== undefined && { first_name: patch.first_name.trim() }),
          ...(patch.last_name !== undefined && { last_name: patch.last_name?.trim() || null }),
          ...(patch.phone !== undefined && { phone: patch.phone?.trim() || null }),
          ...(patch.email !== undefined && { email: patch.email?.trim() || null }),
          ...(patch.roles !== undefined && { roles: patch.roles }),
          ...(patch.notes !== undefined && { notes: patch.notes?.trim() || null }),
          ...(patch.is_active !== undefined && { is_active: patch.is_active }),
        })
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["staff"] });
      toast.success("Staff member updated");
    },
    onError: (e: Error) => toast.error(e.message),
  });
}

// Servers assignment to a quote. One driver lives directly on quotes.driver_id;
// servers use the junction table quote_servers.
export function useQuoteServers(quoteId: string | null | undefined) {
  return useQuery({
    queryKey: ["quote_servers", quoteId],
    enabled: !!quoteId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("quote_servers")
        .select("staff_id, staff:staff(*)")
        .eq("quote_id", quoteId as string);
      if (error) throw error;
      return (data ?? []) as Array<{ staff_id: string; staff: StaffRow }>;
    },
  });
}

/**
 * Replace the set of servers on a quote. Computes an add/remove diff from the
 * provided target set so we don't need to delete-all-then-insert, which would
 * churn history and trip ON DELETE RESTRICT if we'd promoted the junction to
 * carry its own state later.
 */
export function useSetQuoteServers() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ quoteId, staffIds }: { quoteId: string; staffIds: string[] }) => {
      const { data: existing, error: fetchErr } = await supabase
        .from("quote_servers")
        .select("staff_id")
        .eq("quote_id", quoteId);
      if (fetchErr) throw fetchErr;

      const existingIds = new Set((existing ?? []).map((r) => r.staff_id));
      const target = new Set(staffIds);
      const toRemove = Array.from(existingIds).filter((id) => !target.has(id));
      const toAdd = Array.from(target).filter((id) => !existingIds.has(id));

      if (toRemove.length) {
        const { error } = await supabase
          .from("quote_servers")
          .delete()
          .eq("quote_id", quoteId)
          .in("staff_id", toRemove);
        if (error) throw error;
      }
      if (toAdd.length) {
        const { error } = await supabase
          .from("quote_servers")
          .insert(toAdd.map((staff_id) => ({ quote_id: quoteId, staff_id })));
        if (error) throw error;
      }
    },
    onSuccess: (_data, { quoteId }) => {
      qc.invalidateQueries({ queryKey: ["quote_servers", quoteId] });
      qc.invalidateQueries({ queryKey: ["quotes"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });
}
